import React, { useMemo, useState } from 'react';

interface Props {
  users: any[];
  loading: boolean;
  setPremium: (uid: string, value: boolean) => Promise<void> | void;
}

const UsersSection: React.FC<Props> = ({ users, loading, setPremium }) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all'|'premium'|'free'>('all');
  const [premiumType, setPremiumType] = useState<'all'|'manual'|'system'>('all');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(u => {
      if (status === 'premium' && !u.isPremium) return false;
      if (status === 'free' && u.isPremium) return false;
      if (premiumType !== 'all' && (u.premiumType || '') !== premiumType) return false;
      if (fromDate) {
        const from = new Date(fromDate);
        const expires = u.premiumExpiresAt ? new Date(u.premiumExpiresAt) : null;
        if (!expires || expires < from) return false;
      }
      if (toDate) {
        const to = new Date(toDate);
        const expires = u.premiumExpiresAt ? new Date(u.premiumExpiresAt) : null;
        if (!expires || expires > to) return false;
      }
      if (!q) return true;
      return (u.displayName || u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    });
  }, [users, query, status, premiumType, fromDate, toDate]);

  if (loading) return <p>Carregando usuários...</p>;
  return (
    <div className="section-block" aria-live="polite">
      <h3>Gerenciamento de Usuários</h3>

      <div className="users-filters" style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="sr-only">Pesquisar usuários</span>
          <input aria-label="Pesquisar usuários por nome ou email" placeholder="Pesquisar por nome ou email" value={query} onChange={e => setQuery(e.target.value)} />
        </label>

        <label>
          <span className="sr-only">Filtrar por status</span>
          <select aria-label="Filtrar usuários" value={status} onChange={e => setStatus(e.target.value as any)}>
            <option value="all">Todos</option>
            <option value="premium">Premium</option>
            <option value="free">Não Premium</option>
          </select>
        </label>

        <label>
          <span className="sr-only">Filtrar por tipo de premium</span>
          <select aria-label="Filtrar por tipo de premium" value={premiumType} onChange={e => setPremiumType(e.target.value as any)}>
            <option value="all">Todos tipos</option>
            <option value="manual">Manuais</option>
            <option value="system">Sistemáticos</option>
          </select>
        </label>

        <label>
          <span className="muted">Vence de:</span>
          <input type="date" aria-label="Filtrar data de expiração de" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </label>
        <label>
          <span className="muted">Até:</span>
          <input type="date" aria-label="Filtrar data de expiração até" value={toDate} onChange={e => setToDate(e.target.value)} />
        </label>

        <div className="muted" aria-hidden>{filtered.length} resultados</div>
      </div>

      {filtered.length === 0 ? (
        <p>Nenhum usuário encontrado.</p>
      ) : (
        <table className="admin-table compact" role="table" aria-label="Tabela de usuários">
          <thead>
            <tr>
              <th scope="col">Nome</th>
              <th scope="col">Email</th>
              <th scope="col">Premium</th>
              <th scope="col">Início</th>
              <th scope="col">Fim</th>
              <th scope="col">Dias Restantes</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id}>
                <td>{u.displayName || u.name || '-'}</td>
                <td>{u.email || '-'}</td>
                <td>{u.isPremium ? 'Sim' : 'Não'}</td>
                <td>{u.premiumActivatedAt ? new Date(u.premiumActivatedAt).toLocaleDateString('pt-BR') : '-'}</td>
                <td>{u.premiumExpiresAt ? new Date(u.premiumExpiresAt).toLocaleDateString('pt-BR') : '-'}</td>
                <td>{u.isPremium && u.daysRemaining !== undefined ? `${u.daysRemaining} dias` : '-'}</td>
                <td>
                  <button onClick={() => setPremium(u.id, true)} disabled={u.isPremium}>Ativar</button>
                  <button onClick={() => setPremium(u.id, false)} disabled={!u.isPremium}>Desativar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UsersSection;
